import {
  PlusCircle,
  X,
  Upload,
  Image,
  Link,
  Trash2,
  Eye,
  Settings,
  Move,
  Search,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import type {
  CardItemData,
  CategoryItemData,
  CategorySectionData,
  SuggestedProductsSectionData,
  PageLayoutData,
  Section,
} from "../../../../src/types";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { COMPONENT_TYPES } from "../../lib/constants";
import type { ApiResponse, ComponentType } from "../../lib/types";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";

interface Props {
  onSuccess?: () => void;
  layoutData?: PageLayoutData;
}

const PageLayoutForm: React.FC<Props> = ({ onSuccess, layoutData }) => {
  const [formData, setFormData] = useState<PageLayoutData>({
    page: layoutData?.page || "",
    metadata: layoutData?.metadata || [],
  });

  // Update form data when layoutData changes (for edit mode)
  useEffect(() => {
    if (layoutData) {
      setFormData({
        page: layoutData.page || "",
        metadata: layoutData.metadata || [],
      });
    }
  }, [layoutData]);

  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<{
    [key: string]: string;
  }>({});
  const { user } = useAuth();
  const { toast } = useToast();

  // File upload refs
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await api.fetchCategories();
        setCategories(categoriesData || []);
      } catch (error) {
        console.error("Failed to load categories:", error);
        toast({
          title: "Error",
          description: "Failed to load categories",
          variant: "destructive",
        });
      }
    };
    loadCategories();
  }, [toast]);

  const uploadFile = async (
    file: File,
    sectionIndex: number,
    itemIndex?: number,
    field?: string,
  ): Promise<string> => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "page-layout");
      const response = await api.uploadFile(formData);
      if (response && typeof response.url === "string") {
        return response.url;
      }
      throw new Error("Invalid upload response");
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload file. Please try again.",
        variant: "destructive",
      });
      // Always throw to ensure the function never returns undefined
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    sectionIndex: number,
    itemIndex?: number,
    field?: string,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validImageTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description:
          "Please upload a valid image file (JPEG, PNG, GIF, or WebP)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      const url = await uploadFile(file, sectionIndex, itemIndex, field);

      if (itemIndex !== undefined && field) {
        // Update specific item field
        updateItemField(sectionIndex, itemIndex, field, url);
      } else {
        // Update section data
        updateSectionField(sectionIndex, field || "image", url);
      }

      toast({
        title: "Upload successful",
        description: "File uploaded successfully",
      });
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  // Fix updateSectionField return type
  const updateSectionField = (
    sectionIndex: number,
    field: string,
    value: string,
  ) => {
    setFormData(prev => {
      const newMetadata = prev.metadata.map((section, i) => {
        if (i === sectionIndex) {
          if (
            typeof section.data === "object" &&
            !Array.isArray(section.data)
          ) {
            return {
              ...section,
              data: {
                ...section.data,
                [field]: value,
              },
            } as Section;
          }
        }
        return section;
      });
      return {
        ...prev,
        metadata: newMetadata as Section[],
      };
    });
  };

  const updateItemField = (
    sectionIndex: number,
    itemIndex: number,
    field: string,
    value: string,
  ) => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex) {
          if (section.type === "carousel") {
            const newData = [...section.data];
            newData[itemIndex] = {
              ...newData[itemIndex],
              [field]: value,
            };
            return { ...section, data: newData };
          }
          if (section.type === "card") {
            const newData = [...section.data];
            newData[itemIndex] = {
              ...newData[itemIndex],
              [field]: value,
            };
            return { ...section, data: newData };
          }
          if (section.type === "categories") {
            const newItems = [...section.data.items];
            newItems[itemIndex] = {
              ...newItems[itemIndex],
              [field]: value,
            };
            return {
              ...section,
              data: {
                ...section.data,
                items: newItems,
              },
            };
          }
        }
        return section;
      }),
    }));
  };

  const validateSection = (
    section: Section,
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (section.type === "banner") {
      if (!section.data.image) {
        errors.push("Banner image is required");
      }
      if (!section.data.link) {
        errors.push("Banner link is required");
      }
      return { isValid: errors.length === 0, errors };
    }

    if (section.type === "card") {
      if (section.data.length === 0) {
        errors.push("At least one card item is required");
      } else {
        section.data.forEach((item, index) => {
          if (!item.icon) errors.push(`Card ${index + 1}: Icon is required`);
          if (!item.title) errors.push(`Card ${index + 1}: Title is required`);
        });
      }
      return { isValid: errors.length === 0, errors };
    }

    if (section.type === "carousel") {
      if (section.data.length === 0) {
        errors.push("At least one carousel item is required");
      } else {
        section.data.forEach((item, index) => {
          if (!item.image)
            errors.push(`Carousel ${index + 1}: Image is required`);
          if (!item.link)
            errors.push(`Carousel ${index + 1}: Link is required`);
        });
      }
      return { isValid: errors.length === 0, errors };
    }

    if (section.type === "categories") {
      const categoriesData = section.data as CategorySectionData;
      if (!categoriesData.title) {
        errors.push("Categories title is required");
      }
      if (categoriesData.rows < 1 || categoriesData.rows > 10) {
        errors.push("Rows must be between 1 and 10");
      }
      if (categoriesData.columns < 1 || categoriesData.columns > 4) {
        errors.push("Columns must be between 1 and 4");
      }
      if (categoriesData.items.length === 0) {
        errors.push("At least one category item is required");
      } else {
        categoriesData.items.forEach((item, index) => {
          if (!item.image)
            errors.push(`Category ${index + 1}: Image is required`);
          if (!item.title)
            errors.push(`Category ${index + 1}: Title is required`);
          if (!item.link)
            errors.push(`Category ${index + 1}: Link is required`);
        });
      }
      return { isValid: errors.length === 0, errors };
    }

    if (section.type === "suggested_products") {
      const suggestedProductsData =
        section.data as SuggestedProductsSectionData;
      if (!suggestedProductsData.title) {
        errors.push("Suggested products title is required");
      }
      if (!suggestedProductsData.subtitle) {
        errors.push("Suggested products subtitle is required");
      }
      if (!suggestedProductsData.dataApi) {
        errors.push("Data API endpoint is required");
      }
      return { isValid: errors.length === 0, errors };
    }

    return { isValid: false, errors: ["Invalid section type"] };
  };

  const addNewSection = (): void => {
    setFormData(prev => ({
      ...prev,
      metadata: [
        ...prev.metadata,
        {
          type: "banner",
          data: { image: "", link: "" },
        },
      ],
    }));
    setActiveSection(formData.metadata.length);
  };

  const removeSection = (index: number): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.filter((_, i) => i !== index),
    }));
    if (activeSection >= index) {
      setActiveSection(Math.max(0, activeSection - 1));
    }
  };

  const moveSection = (fromIndex: number, toIndex: number): void => {
    setFormData(prev => {
      const newMetadata = [...prev.metadata];
      const [movedSection] = newMetadata.splice(fromIndex, 1);
      newMetadata.splice(toIndex, 0, movedSection);
      return {
        ...prev,
        metadata: newMetadata,
      };
    });
  };

  const updateSectionType = (index: number, type: ComponentType): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === index) {
          if (type === "banner") {
            return {
              type: "banner",
              data: { image: "", link: "" },
            };
          }
          if (type === "card") {
            return {
              type: "card",
              data: [{ icon: "", title: "", subtitle: "" }],
            };
          }
          if (type === "carousel") {
            return {
              type: "carousel",
              data: [{ image: "", link: "" }],
            };
          }
          if (type === "categories") {
            return {
              type: "categories",
              data: { title: "", caption: "", rows: 2, columns: 4, items: [] },
            };
          }
          if (type === "suggested_products") {
            return {
              type: "suggested_products",
              data: { title: "", subtitle: "", dataApi: "" },
            };
          }
        }
        return section;
      }),
    }));
  };

  const addCarouselItem = (sectionIndex: number): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "carousel") {
          return {
            ...section,
            data: [...section.data, { image: "", link: "" }],
          };
        }
        return section;
      }),
    }));
  };

  const removeCarouselItem = (
    sectionIndex: number,
    itemIndex: number,
  ): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "carousel") {
          return {
            ...section,
            data: section.data.filter((_, idx) => idx !== itemIndex),
          };
        }
        return section;
      }),
    }));
  };

  const addCardItem = (sectionIndex: number): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "card") {
          return {
            ...section,
            data: [...section.data, { icon: "", title: "", subtitle: "" }],
          };
        }
        return section;
      }),
    }));
  };

  const removeCardItem = (sectionIndex: number, itemIndex: number): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "card") {
          return {
            ...section,
            data: section.data.filter((_, idx) => idx !== itemIndex),
          };
        }
        return section;
      }),
    }));
  };

  const addCategoryItem = (sectionIndex: number): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "categories") {
          return {
            ...section,
            data: {
              ...section.data,
              items: [
                ...section.data.items,
                { image: "", title: "", link: "" },
              ],
            },
          };
        }
        return section;
      }),
    }));
  };

  const removeCategoryItem = (
    sectionIndex: number,
    itemIndex: number,
  ): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "categories") {
          return {
            ...section,
            data: {
              ...section.data,
              items: section.data.items.filter((_, idx) => idx !== itemIndex),
            },
          };
        }
        return section;
      }),
    }));
  };

  const updateCategorySettings = (
    sectionIndex: number,
    field: keyof CategorySectionData,
    value: string | number,
  ): void => {
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.map((section, i) => {
        if (i === sectionIndex && section.type === "categories") {
          return {
            ...section,
            data: {
              ...section.data,
              [field]: value,
            },
          };
        }
        return section;
      }),
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError("");

    try {
      // Validate page name
      if (!formData.page || formData.page.trim() === "") {
        setError("Page name is required");
        return;
      }

      // Validate page name format (alphanumeric, hyphens, underscores)
      const pageNameRegex = /^[a-zA-Z0-9_-]+$/;
      if (!pageNameRegex.test(formData.page.trim())) {
        setError(
          "Page name can only contain letters, numbers, hyphens, and underscores",
        );
        return;
      }

      // Validate all sections
      const allErrors: string[] = [];
      formData.metadata.forEach((section, index) => {
        const validation = validateSection(section);
        if (!validation.isValid) {
          validation.errors.forEach(error => {
            allErrors.push(`Section ${index + 1}: ${error}`);
          });
        }
      });

      if (allErrors.length > 0) {
        setError(allErrors[0]); // Show first error
        toast({
          title: "Validation failed",
          description: "Please fix all errors before saving",
          variant: "destructive",
        });
        return;
      }

      let response: ApiResponse<null>;

      if (layoutData?.id) {
        response = await api.updatePageLayout(layoutData.id, {
          page: formData.page.trim(),
          metadata: formData.metadata,
        });
      } else {
        response = await api.createPageLayout({
          page: formData.page.trim(),
          metadata: formData.metadata,
        });
      }

      if (response && "error" in response) {
        toast({
          title: "Failed to save page layout",
          description: response.error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: layoutData?.id
            ? "Layout updated successfully"
            : "Layout created successfully",
        });
        onSuccess?.();
      }
    } catch (err: any) {
      toast({
        title: "Failed to save page layout",
        description: err.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const renderBannerSection = (section: Section, sectionIndex: number) => {
    if (section.type !== "banner") return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image size={20} />
            Banner Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderSectionOptions(section, sectionIndex)}
          <div className="space-y-2">
            <Label>Banner Image</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Image URL or upload file"
                value={section.data.image}
                onChange={e =>
                  updateSectionField(sectionIndex, "image", e.target.value)
                }
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const input = fileInputRefs.current[`banner-${sectionIndex}`];
                  if (input) input.click();
                }}
                disabled={isUploading}
              >
                <Upload size={16} />
              </Button>
              <input
                ref={el => {
                  fileInputRefs.current[`banner-${sectionIndex}`] = el;
                }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e =>
                  handleFileUpload(e, sectionIndex, undefined, "image")
                }
              />
            </div>
            {section.data.image && (
              <div className="mt-2">
                <img
                  src={section.data.image}
                  alt="Banner preview"
                  className="w-full h-32 object-cover rounded-md"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Link URL</Label>
            <Input
              placeholder="https://example.com"
              value={section.data.link}
              onChange={e =>
                updateSectionField(sectionIndex, "link", e.target.value)
              }
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCarouselSection = (section: Section, sectionIndex: number) => {
    if (section.type !== "carousel") return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image size={20} />
            Carousel Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderSectionOptions(section, sectionIndex)}
          {section.data.map((item, itemIndex) => (
            <div
              key={`carousel-${sectionIndex}-${itemIndex}-${
                item.image?.slice(0, 10) || "no-image"
              }`}
              className="border rounded-lg p-4 space-y-4"
            >
              <div className="flex justify-between items-center">
                <Badge variant="secondary">Slide {itemIndex + 1}</Badge>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => removeCarouselItem(sectionIndex, itemIndex)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Image URL or upload file"
                    value={item.image}
                    onChange={e =>
                      updateItemField(
                        sectionIndex,
                        itemIndex,
                        "image",
                        e.target.value,
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const input =
                        fileInputRefs.current[
                          `carousel-${sectionIndex}-${itemIndex}`
                        ];
                      if (input) input.click();
                    }}
                    disabled={isUploading}
                  >
                    <Upload size={16} />
                  </Button>
                  <input
                    ref={el => {
                      fileInputRefs.current[
                        `carousel-${sectionIndex}-${itemIndex}`
                      ] = el;
                    }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e =>
                      handleFileUpload(e, sectionIndex, itemIndex, "image")
                    }
                  />
                </div>
                {item.image && (
                  <div className="mt-2">
                    <img
                      src={item.image}
                      alt={`Carousel ${itemIndex + 1} preview`}
                      className="w-full h-24 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Link URL</Label>
                <Input
                  placeholder="https://example.com"
                  value={item.link}
                  onChange={e =>
                    updateItemField(
                      sectionIndex,
                      itemIndex,
                      "link",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addCarouselItem(sectionIndex)}
            className="w-full"
          >
            <PlusCircle size={16} className="mr-2" />
            Add Carousel Item
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderCardSection = (section: Section, sectionIndex: number) => {
    if (section.type !== "card") return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings size={20} />
            Card Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderSectionOptions(section, sectionIndex)}
          {section.data.map((item, itemIndex) => (
            <div
              key={`card-${sectionIndex}-${itemIndex}-${
                item.title?.slice(0, 10) || "no-title"
              }`}
              className="border rounded-lg p-4 space-y-4"
            >
              <div className="flex justify-between items-center">
                <Badge variant="secondary">Card {itemIndex + 1}</Badge>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => removeCardItem(sectionIndex, itemIndex)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Icon URL or upload file"
                    value={item.icon}
                    onChange={e =>
                      updateItemField(
                        sectionIndex,
                        itemIndex,
                        "icon",
                        e.target.value,
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const input =
                        fileInputRefs.current[
                          `card-icon-${sectionIndex}-${itemIndex}`
                        ];
                      if (input) input.click();
                    }}
                    disabled={isUploading}
                  >
                    <Upload size={16} />
                  </Button>
                  <input
                    ref={el => {
                      fileInputRefs.current[
                        `card-icon-${sectionIndex}-${itemIndex}`
                      ] = el;
                    }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e =>
                      handleFileUpload(e, sectionIndex, itemIndex, "icon")
                    }
                  />
                </div>
                {item.icon && (
                  <div className="mt-2">
                    <img
                      src={item.icon}
                      alt={`Card ${itemIndex + 1} icon preview`}
                      className="w-12 h-12 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  placeholder="Card title"
                  value={item.title}
                  onChange={e =>
                    updateItemField(
                      sectionIndex,
                      itemIndex,
                      "title",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Subtitle</Label>
                <Input
                  placeholder="Card subtitle"
                  value={item.subtitle}
                  onChange={e =>
                    updateItemField(
                      sectionIndex,
                      itemIndex,
                      "subtitle",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addCardItem(sectionIndex)}
            className="w-full"
          >
            <PlusCircle size={16} className="mr-2" />
            Add Card Item
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderCategoriesSection = (section: Section, sectionIndex: number) => {
    if (section.type !== "categories") return null;

    const categoriesData = section.data as CategorySectionData;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings size={20} />
            Categories Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderSectionOptions(section, sectionIndex)}
          <div className="space-y-2">
            <Label>Section Title</Label>
            <Input
              placeholder="e.g., Categories, Shop by Category"
              value={categoriesData.title}
              onChange={e =>
                updateCategorySettings(sectionIndex, "title", e.target.value)
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Section Caption (Optional)</Label>
            <Input
              placeholder="e.g., Browse our wide selection of products"
              value={categoriesData.caption || ""}
              onChange={e =>
                updateCategorySettings(sectionIndex, "caption", e.target.value)
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rows</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={categoriesData.rows}
                onChange={e => {
                  const value = Math.max(
                    1,
                    Math.min(10, Number(e.target.value)),
                  );
                  updateCategorySettings(sectionIndex, "rows", value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Columns</Label>
              <Input
                type="number"
                min="1"
                max="4"
                value={categoriesData.columns}
                onChange={e => {
                  const value = Math.max(
                    1,
                    Math.min(4, Number(e.target.value)),
                  );
                  updateCategorySettings(sectionIndex, "columns", value);
                }}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">
                  Category Items
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Add categories that users can browse and tap on
                </p>
              </div>
              <Badge variant="outline" className="text-xs">
                {categoriesData.items.length} items
              </Badge>
            </div>
            {categoriesData.items.map((item, itemIndex) => (
              <div
                key={`category-item-${sectionIndex}-${itemIndex}-${categoriesData.items.length}`}
                className="border rounded-lg p-6 space-y-6 bg-gray-50/50"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm">
                      Category {itemIndex + 1}
                    </Badge>
                    {item.title && (
                      <span className="text-sm text-gray-600">
                        • {item.title}
                      </span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeCategoryItem(sectionIndex, itemIndex)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Category Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Image URL or upload file"
                        value={item.image}
                        onChange={e =>
                          updateItemField(
                            sectionIndex,
                            itemIndex,
                            "image",
                            e.target.value,
                          )
                        }
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const input =
                            fileInputRefs.current[
                              `category-${sectionIndex}-${itemIndex}`
                            ];
                          if (input) input.click();
                        }}
                        disabled={isUploading}
                        className="shrink-0"
                      >
                        <Upload size={16} />
                      </Button>
                      <input
                        ref={el => {
                          fileInputRefs.current[
                            `category-${sectionIndex}-${itemIndex}`
                          ] = el;
                        }}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={e =>
                          handleFileUpload(e, sectionIndex, itemIndex, "image")
                        }
                      />
                    </div>
                    {item.image && (
                      <div className="mt-2">
                        <img
                          src={item.image}
                          alt={`Category ${itemIndex + 1} preview`}
                          className="w-20 h-20 object-cover rounded-md border"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Category Title
                      </Label>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-600">
                            Select from existing categories
                          </Label>
                          <Select
                            value=""
                            onValueChange={value => {
                              const selectedCategory = categories.find(
                                cat => cat.id === value,
                              );
                              if (selectedCategory) {
                                updateItemField(
                                  sectionIndex,
                                  itemIndex,
                                  "title",
                                  selectedCategory.name,
                                );
                                updateItemField(
                                  sectionIndex,
                                  itemIndex,
                                  "link",
                                  `/category/${selectedCategory.id}`,
                                );
                              }
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Choose a category to auto-fill title and link" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(category => (
                                <SelectItem
                                  key={category.id}
                                  value={category.id}
                                >
                                  {category.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-center text-sm">
                            <span className="bg-gray-50 px-2 text-gray-500">
                              or
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-600">
                            Enter custom title
                          </Label>
                          <Input
                            placeholder="Type your custom category title"
                            value={item.title}
                            onChange={e =>
                              updateItemField(
                                sectionIndex,
                                itemIndex,
                                "title",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Link URL</Label>
                      <div className="space-y-2">
                        <Input
                          placeholder="Will be auto-filled when category is selected"
                          value={item.link}
                          onChange={e =>
                            updateItemField(
                              sectionIndex,
                              itemIndex,
                              "link",
                              e.target.value,
                            )
                          }
                        />
                        {item.link && (
                          <p className="text-xs text-gray-500">
                            This link will be used when users tap on this
                            category
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button
              type="button"
              onClick={() => addCategoryItem(sectionIndex)}
              className="w-full border-2 border-dashed border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-700 transition-colors"
              variant="outline"
            >
              <PlusCircle size={16} className="mr-2" />
              Add Category Item
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSuggestedProductsSection = (
    section: Section,
    sectionIndex: number,
  ) => {
    if (section.type !== "suggested_products") return null;

    const suggestedProductsData = section.data as SuggestedProductsSectionData;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings size={20} />
            Suggested Products Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderSectionOptions(section, sectionIndex)}
          <div className="space-y-2">
            <Label>Section Title</Label>
            <Input
              placeholder="e.g., Recommended for You, Trending Products"
              value={suggestedProductsData.title}
              onChange={e =>
                updateSectionField(sectionIndex, "title", e.target.value)
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Section Subtitle</Label>
            <Input
              placeholder="e.g., Based on your preferences"
              value={suggestedProductsData.subtitle}
              onChange={e =>
                updateSectionField(sectionIndex, "subtitle", e.target.value)
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Data API Endpoint</Label>
            <Input
              placeholder="e.g., /api/products/suggested, /api/products/trending"
              value={suggestedProductsData.dataApi}
              onChange={e =>
                updateSectionField(sectionIndex, "dataApi", e.target.value)
              }
            />
            <p className="text-xs text-gray-500">
              API endpoint that will be used to fetch product data for this
              section
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSection = (section: Section, sectionIndex: number) => {
    switch (section.type) {
      case "banner":
        return renderBannerSection(section, sectionIndex);
      case "carousel":
        return renderCarouselSection(section, sectionIndex);
      case "card":
        return renderCardSection(section, sectionIndex);
      case "categories":
        return renderCategoriesSection(section, sectionIndex);
      case "suggested_products":
        return renderSuggestedProductsSection(section, sectionIndex);
      default:
        return null;
    }
  };

  // In each renderSection, add UI for padding and background color
  const renderSectionOptions = (section: Section, sectionIndex: number) => {
    // Safe access for padding/backgroundColor
    const padding =
      typeof section.data === "object" &&
      !Array.isArray(section.data) &&
      "padding" in section.data
        ? (section.data as any).padding
        : "p-4";
    const backgroundColor =
      typeof section.data === "object" &&
      !Array.isArray(section.data) &&
      "backgroundColor" in section.data
        ? (section.data as any).backgroundColor
        : "#ffffff";
    const paddingId = `padding-${sectionIndex}`;
    const bgId = `bg-${sectionIndex}`;
    return (
      <div className="flex gap-4 mb-4">
        <div>
          <label htmlFor={paddingId} className="block text-xs font-medium mb-1">
            Padding
          </label>
          <select
            id={paddingId}
            className="border rounded px-2 py-1 text-sm"
            value={padding}
            onChange={e =>
              updateSectionField(sectionIndex, "padding", e.target.value)
            }
          >
            {/* PADDING_OPTIONS.map(opt => ( */}
            <option value="p-0">None</option>
            <option value="p-2">Small</option>
            <option value="p-4">Medium</option>
            <option value="p-8">Large</option>
          </select>
        </div>
        <div>
          <label htmlFor={bgId} className="block text-xs font-medium mb-1">
            Background Color
          </label>
          <input
            id={bgId}
            type="color"
            className="w-8 h-8 p-0 border rounded"
            value={backgroundColor}
            onChange={e =>
              updateSectionField(
                sectionIndex,
                "backgroundColor",
                e.target.value,
              )
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Page Name Input */}
        <Card>
          <CardHeader>
            <CardTitle>Page Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="pageName">Page Name</Label>
              <Input
                id="pageName"
                type="text"
                value={formData.page}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    page: e.target.value,
                  }))
                }
                placeholder="Enter page name (e.g., home, categories, products)"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Page Sections</h3>
            <Button type="button" onClick={addNewSection}>
              <PlusCircle size={16} className="mr-2" />
              Add Section
            </Button>
          </div>

          {/* Section Management */}
          {formData.metadata.length > 0 && (
            <Card className="border-dashed border-2 border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings size={16} />
                  Section Order
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Drag sections to reorder or use the controls below
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {formData.metadata.map((section, index) => (
                  <div
                    key={`section-manager-${index}-${section.type}-${formData.metadata.length}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-mono bg-white px-2 py-1 rounded border text-xs">
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-700">
                          {section.type.charAt(0).toUpperCase() +
                            section.type.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setActiveSection(index)}
                        className="text-xs"
                      >
                        Edit
                      </Button>
                      <div className="flex gap-1">
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSection(index, index - 1)}
                            title="Move Up"
                            className="h-8 w-8 p-0"
                          >
                            ↑
                          </Button>
                        )}
                        {index < formData.metadata.length - 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSection(index, index + 1)}
                            title="Move Down"
                            className="h-8 w-8 p-0"
                          >
                            ↓
                          </Button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSection(index)}
                        title="Remove Section"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {formData.metadata.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-gray-500">
                  <Settings size={48} className="mx-auto mb-4 opacity-50" />
                  <p>
                    No sections added yet. Click "Add Section" to get started.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Tabs
              value={activeSection.toString()}
              onValueChange={value => setActiveSection(Number(value))}
            >
              <TabsList className="grid w-full grid-cols-4">
                {formData.metadata.map((section, index) => (
                  <TabsTrigger
                    key={`section-tab-${index}-${section.type}`}
                    value={index.toString()}
                  >
                    {section.type.charAt(0).toUpperCase() +
                      section.type.slice(1)}{" "}
                    {index + 1}
                  </TabsTrigger>
                ))}
              </TabsList>

              {formData.metadata.map((section, sectionIndex) => (
                <TabsContent
                  key={`section-content-${sectionIndex}-${section.type}`}
                  value={sectionIndex.toString()}
                >
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Select
                          value={section.type}
                          onValueChange={value =>
                            updateSectionType(
                              sectionIndex,
                              value as ComponentType,
                            )
                          }
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPONENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {renderSection(section, sectionIndex)}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        {error && (
          <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <Button type="submit" className="w-full" disabled={isUploading}>
          {isUploading ? "Uploading..." : "Save Layout"}
        </Button>
      </form>
    </div>
  );
};

export default PageLayoutForm;
